import React, { useState, useEffect } from "react";
import {
    Button,
    Typography,
    Card,
    CardHeader,
    CardBody,
    IconButton,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Avatar,
    Tooltip,
    Progress,
} from "@material-tailwind/react";
import { StatisticsCard } from "../../widgets/cards";
import { StatisticsChart } from "../../widgets/charts";
import { CheckCircleIcon, ClockIcon, CurrencyDollarIcon } from "@heroicons/react/24/solid";
import DatePicker from 'react-datepicker';
import { es } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";

import SideNav from "../layout/sidenav";
import NavBar from "../layout/navbar";
import Footer from "../layout/footer";
import axios from 'axios';

const Dashboard = ({ routes, username, role }) => {
    const today = formatDate(new Date());

    const [openSidenav, setOpenSidenav] = useState(false);
    const [totalsByType, setTotalsByType] = useState(null);
    const [chartUserData, setChartUserData] = useState(null);
    const [chartDateData, setChartDateData] = useState(null);
    const [chartTotalsData, setChartTotalsData] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [displayTitle, setDisplayTitle] = useState(today)

    useEffect(() => {
        fetchDashboardData('','')
    }, []);

    const fetchDashboardData = (start_date, end_date) => {
        axios.get('/dashboard/fetch_data', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const { totals_by_user, invoices_by_type, totals_by_date, invoices_count_by_date } = response.data;

                setTotalsByType(invoices_by_type);
                setChartUserData(totals_by_user);
                setChartDateData(totals_by_date);
                setChartTotalsData(invoices_count_by_date);
            })
            .catch(error => {
                console.error('Error fetching data:', error);
            });
    };

    const handleDateChange = () => {
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);
        const title = startDate || endDate
            ? `${formattedStartDate ? formattedStartDate : '...'} a ${formattedEndDate ? formattedEndDate : '...'}`
            : `${today}`;

        setDisplayTitle(title);

        fetchDashboardData(startDate, endDate);
    };

    const getChartConfig = (data, name, key, value_type) => {
        if (!data || data.length === 0) return null;

        const categories = data.map(item => item[key]);
        const chartData = data.map(item => Math.ceil(item.total) || Math.ceil(item.count));

        if (key === 'username') {
            return {
                type: "bar",
                height: 220,
                series: [
                    {
                        name: '',
                        data: chartData,
                    },
                ],
                options: {
                    colors: ["#388e3c"],
                    plotOptions: {
                        bar: {
                            columnWidth: "20%",
                            borderRadius: 5,
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    xaxis: {
                        categories: categories.map(category =>
                            category ? category.toUpperCase() : category
                        ),
                    },
                    yaxis: {
                        labels: {
                            formatter: function (val) {
                                return formatMoney(val)/*.toFixed(2)*/;
                            }
                        }
                    },
                },
            };
        } else {
            return {
                type: "line",
                height: 220,
                series: [
                    {
                        name: '',
                        data: chartData,
                    },
                ],
                options: {
                    colors: ["#0288d1"],
                    stroke: {
                        lineCap: "round",
                    },
                    markers: {
                        size: 5,
                    },
                    xaxis: {
                        categories: categories.map(category =>
                            category ? category.toUpperCase() : category
                        ),
                    },
                    yaxis: {
                        labels: {
                            formatter: function (val) {
                                if (value_type === 'integer') {
                                    return val;
                                } else {
                                    return formatMoney(val)/*.toFixed(2)*/;
                                }
                            }
                        }
                    },
                },
            };
        }
    };

    // Generate chart configurations
    const userChartConfig = getChartConfig(chartUserData, "TOTAL RECAUDACIÓN POR CAJA", 'username', 'float');
    const dateChartConfig = getChartConfig(chartDateData, "TOTAL RECAUDACIÓN POR DIA", 'date', 'float');
    const totalChartConfig = getChartConfig(chartTotalsData, "TOTAL DE PREDIOS PAGADOS", 'date', 'integer');

    return (
        <>
            <div className="mt-12">

                <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
                    <div>
                        <h1 className="text-2xl font-bold">¡Hola {username.toUpperCase()}!</h1>
                        <h5 className="text-md text-blue-gray-900">{displayTitle}</h5>
                    </div>
                    <div className="flex items-center gap-2">
                        {(startDate || endDate) &&
                            <button
                                onClick={() => {
                                    setStartDate("");
                                    setEndDate("");
                                    setDisplayTitle(today);
                                    fetchDashboardData('','');
                                }}
                                className="text-xl p-2 bg-gray-200 rounded-full h-auto"
                                aria-label="Reset"
                            >
                                ✕
                            </button>
                        }
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            className="border rounded px-4 py-2"
                            locale={es}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Fecha de inicio"
                        />
                        <span>a</span>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            className="border rounded px-4 py-2"
                            locale={es}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Fecha de fin"
                        />
                        <button
                            onClick={handleDateChange}
                            className="ml-2 bg-black text-white px-4 py-2 rounded"
                        >
                            Filtrar
                        </button>
                    </div>
                </div>


                <div className="mb-12 grid gap-y-10 gap-x-6 md:grid-cols-2 xl:grid-cols-4">
                    {totalsByType && Object.values(totalsByType).map(({description, total}) => (
                        <StatisticsCard
                            key={description}
                            title={description}
                            value={`${formatMoney(Math.ceil(total))}`}
                            color={'gray'}
                            icon={
                                <CurrencyDollarIcon className="w-6 h-6 text-white"/>
                            }
                        />
                    ))}
                </div>

                <div className="mb-6 grid grid-cols-1 gap-y-12 gap-x-6 md:grid-cols-2 xl:grid-cols-3">
                    {userChartConfig ? (
                        <StatisticsChart
                            title="TOTAL RECAUDACIÓN POR CAJA"
                            chart={userChartConfig}
                        />
                    ) : (
                        <div className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                            No hay registros en la fecha actual.
                        </div>
                    )}

                    {dateChartConfig ? (
                        <StatisticsChart
                            title="TOTAL RECAUDACIÓN POR DÍA"
                            chart={dateChartConfig}
                        />
                    ) : (
                        <div className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                            No hay registros en la fecha actual.
                        </div>
                    )}

                    {totalChartConfig ? (
                        <StatisticsChart
                            title="TOTAL DE PREDIOS PAGADOS"
                            chart={totalChartConfig}
                        />
                    ) : (
                        <div className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                            No hay registros en la fecha actual.
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const formatDate = (dateString) => {
    if (!dateString) return "";

    // Check if it's already a Date object, and if not, parse it.
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based, so add 1
    const day = date.getDate().toString().padStart(2, '0'); // Ensure the day is two digits

    return `${day}/${month}/${year}`;
};
const formatMoney = (amount) => {
    if (!amount) return "$0";
    const roundedAmount = Math.ceil(amount);
    return `$${roundedAmount.toLocaleString('es-MX', {
        //minimumFractionDigits: 2,
        maximumFractionDigits: 0
    })}`;
};
export default Dashboard;

